import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { createHostEvent, updateHostEvent, fetchEvent, fetchHostEventTypes, uploadEventImage, eventImageUrl, formatApiErrorDetail } from "@/lib/api";

const CITIES = ["Houston", "Atlanta", "Dallas", "Los Angeles", "New Orleans", "New York", "San Diego", "Golden Triangle"];
const CATEGORIES = ["Tasting", "Pop-Up", "Dinner", "Festival", "Class / Workshop", "Live Music", "Happy Hour", "Market", "Fundraiser", "Grand Opening", "Cook-Off", "Other"];

export default function PostEventForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hostTypes, setHostTypes] = useState([]);
  const [form, setForm] = useState({
    name: "", category: "Tasting", host_name: "", host_type: "Restaurant",
    short_description: "", long_description: "", cover_image: "",
    date: "", end_date: "", start_time: "", end_time: "",
    city: "Houston", venue: "", location: "",
    ticket_type: "free", ticket_price: "Free", ticket_url: "",
    capacity: "", sponsors: "",
  });

  useEffect(() => {
    fetchHostEventTypes().then((d) => setHostTypes(d.host_types || [])).catch(() => {});
    if (editing) {
      fetchEvent(id)
        .then((e) => setForm({
          name: e.name || "", category: e.category || "Tasting", host_name: e.host_name || "",
          host_type: e.host_type || "Restaurant", short_description: e.short_description || "",
          long_description: e.long_description || "", cover_image: e.cover_image || "",
          date: (e.date || "").slice(0, 10), end_date: (e.end_date || "").slice(0, 10),
          start_time: e.start_time || "", end_time: e.end_time || "",
          city: e.city || "Houston", venue: e.venue || "", location: e.location || "",
          ticket_type: e.ticket_type || "free", ticket_price: e.ticket_price || "Free",
          ticket_url: e.ticket_url || "", capacity: e.capacity || "",
          sponsors: (e.sponsors || []).join(", "),
        }))
        .catch(() => navigate("/dashboard"));
    }
  }, [editing, id, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onImageUpload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadEventImage(fd);
      setForm((f) => ({ ...f, cover_image: eventImageUrl(res.path) }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payload = {
      ...form,
      capacity: form.capacity ? parseInt(form.capacity, 10) : null,
      end_date: form.end_date || null,
      sponsors: form.sponsors.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      const result = editing ? await updateHostEvent(id, payload) : await createHostEvent(payload);
      toast.success(editing ? "Event updated — pending review" : "Event submitted for review!");
      navigate(editing ? `/events/${result.id}` : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full border border-sand-300 px-4 py-3 bg-white focus:border-brand-500 outline-none";
  const labelCls = "block font-mono text-[10px] tracking-[0.2em] uppercase text-sand-700 mb-2";

  return (
    <div data-testid="post-event-page" className="max-w-2xl mx-auto px-6 py-16 md:py-24">
      <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-4">
        {editing ? "Edit Event" : "Host an Event"}
      </div>
      <h1 className="font-serif font-black text-4xl md:text-5xl leading-[1.02] text-sand-900 mb-3">
        {editing ? "Update your event." : "Put your event on the map."}
      </h1>
      <p className="text-sand-700 font-light mb-10">
        Submissions are reviewed by our editors before going live across the network.
      </p>

      <form onSubmit={submit} className="space-y-6">
        {error && (
          <div data-testid="post-event-error" className="bg-brand-50 border border-brand-500 text-brand-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}
        <div>
          <label className={labelCls}>Event Name</label>
          <input data-testid="event-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Category</label>
            <select data-testid="event-category-select" value={form.category} onChange={set("category")} className={inputCls}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>City</label>
            <select data-testid="event-city-select" value={form.city} onChange={set("city")} className={inputCls}>
              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Host / Business Name</label>
            <input data-testid="event-host-name-input" required value={form.host_name} onChange={set("host_name")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Host Type</label>
            <select data-testid="event-host-type-select" value={form.host_type} onChange={set("host_type")} className={inputCls}>
              {(hostTypes.length ? hostTypes : [form.host_type]).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={labelCls}>Short Description</label>
          <input data-testid="event-short-desc-input" required value={form.short_description} onChange={set("short_description")} className={inputCls} placeholder="One-line hook" />
        </div>
        <div>
          <label className={labelCls}>Full Details</label>
          <textarea data-testid="event-long-desc-input" rows={5} value={form.long_description} onChange={set("long_description")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Cover Image</label>
          {form.cover_image && (
            <div className="mb-3 aspect-[16/9] max-w-sm bg-sand-100 border border-sand-200 overflow-hidden">
              <img src={form.cover_image} alt="Cover preview" data-testid="event-cover-preview" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <label className={`inline-flex items-center justify-center gap-2 border border-sand-300 px-4 py-3 font-mono text-[10px] tracking-[0.2em] uppercase cursor-pointer hover:border-brand-500 transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
              <input type="file" accept="image/*" onChange={onImageUpload} data-testid="event-cover-upload" className="hidden" />
              {uploading ? "Uploading…" : "Upload Image"}
            </label>
            <input data-testid="event-cover-input" value={form.cover_image} onChange={set("cover_image")} className={inputCls} placeholder="…or paste an image URL" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Start Date</label>
            <input type="date" data-testid="event-date-input" required value={form.date} onChange={set("date")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>End Date (optional)</label>
            <input type="date" data-testid="event-end-date-input" value={form.end_date} onChange={set("end_date")} className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Start Time</label>
            <input data-testid="event-start-time-input" value={form.start_time} onChange={set("start_time")} className={inputCls} placeholder="6:00 PM" />
          </div>
          <div>
            <label className={labelCls}>End Time</label>
            <input data-testid="event-end-time-input" value={form.end_time} onChange={set("end_time")} className={inputCls} placeholder="9:00 PM" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Venue</label>
            <input data-testid="event-venue-input" value={form.venue} onChange={set("venue")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Address / Location</label>
            <input data-testid="event-location-input" value={form.location} onChange={set("location")} className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className={labelCls}>Ticketing</label>
            <select data-testid="event-ticket-type-select" value={form.ticket_type} onChange={(e) => setForm((f) => ({ ...f, ticket_type: e.target.value, ticket_url: e.target.value === "external" ? f.ticket_url : "" }))} className={inputCls}>
              <option value="free">Free RSVP</option>
              <option value="external">External Ticket Link</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Price Label</label>
            <input data-testid="event-price-input" value={form.ticket_price} onChange={set("ticket_price")} className={inputCls} placeholder="Free / $25" />
          </div>
          <div>
            <label className={labelCls}>Capacity (optional)</label>
            <input type="number" min="1" data-testid="event-capacity-input" value={form.capacity} onChange={set("capacity")} className={inputCls} />
          </div>
        </div>
        {form.ticket_type === "external" && (
          <div>
            <label className={labelCls}>Ticket URL (Eventbrite, Tock, etc.)</label>
            <input data-testid="event-ticket-url-input" value={form.ticket_url} onChange={set("ticket_url")} className={inputCls} placeholder="https://…" />
          </div>
        )}
        <div>
          <label className={labelCls}>Sponsors (comma separated)</label>
          <input data-testid="event-sponsors-input" value={form.sponsors} onChange={set("sponsors")} className={inputCls} />
        </div>

        <button
          data-testid="event-submit-button"
          disabled={busy}
          className="w-full bg-sand-900 text-sand-50 px-5 py-4 font-mono text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-brand-500 transition-colors disabled:opacity-60"
        >
          {busy ? "Saving…" : editing ? "Save Changes" : "Submit for Review"}
        </button>
      </form>
    </div>
  );
}
